/**
 * Timeline State Management - Conversation context and state persistence
 * 
 * This service manages timeline state across editing sessions, maintains conversation context,
 * and provides seamless continuation of editing workflows. It tracks editing history,
 * user preferences, and project-specific context to enable intelligent AI assistance.
 */

import { debounce } from 'lodash';

class TimelineStateManager {
  constructor() {
    // Core state
    this.currentTimelineState = null;
    this.editingHistory = [];
    this.conversationContext = [];
    this.userPreferences = {};
    this.projectContext = {};
    
    // Session management
    this.sessionId = this._generateSessionId();
    this.lastActivity = Date.now();
    this.isActive = true;
    
    // State persistence
    this.persistenceKey = 'fnctionos_timeline_state';
    this.maxHistoryLength = 50;
    this.maxContextLength = 20;
    
    // Auto-save configuration
    this.autoSaveEnabled = true;
    this.autoSaveDelay = 2000; // 2 seconds
    this.debouncedSave = debounce(() => this._persistState(), this.autoSaveDelay);
    
    // State change listeners
    this.listeners = new Map();
    this.listenerIdCounter = 0;
    
    // Initialize from storage
    this._loadPersistedState();
    
    // Setup auto-cleanup
    this._setupSessionCleanup();
  }

  /**
   * Initialize timeline state for a project
   */
  initializeProject(projectId, projectData) {
    this.projectContext = {
      projectId,
      projectName: projectData.name || 'Untitled Project',
      createdAt: projectData.created_at,
      lastModified: projectData.updated_at || Date.now(),
      clips: projectData.clips || [],
      timeline: projectData.timeline || {},
      chatSessionId: projectData.chat_session_id || this._generateSessionId()
    };

    this.currentTimelineState = {
      clips: projectData.clips || [],
      duration: projectData.timeline?.duration || 0,
      aspectRatio: projectData.timeline?.aspectRatio || '16:9',
      currentTime: 0,
      selectedClipIds: [],
      playbackState: 'paused',
      zoom: 1.0,
      viewportStart: 0,
      lastEditTimestamp: Date.now()
    };

    // Load project-specific conversation context
    this._loadProjectConversationContext(projectId);
    
    // Record project initialization
    this._recordStateChange('project_initialized', {
      projectId,
      projectName: this.projectContext.projectName,
      clipCount: this.currentTimelineState.clips.length
    });

    this._notifyListeners('project_initialized', this.projectContext);
    this._scheduleAutoSave();
  }

  /**
   * Update timeline state with new data
   */
  updateTimelineState(updates, source = 'user_action') {
    if (!this.currentTimelineState) {
      console.warn('Timeline state not initialized');
      return;
    }

    const previousState = { ...this.currentTimelineState };
    const timestamp = Date.now();

    // Apply updates
    Object.assign(this.currentTimelineState, updates, {
      lastEditTimestamp: timestamp
    });

    // Record the change in editing history
    this._recordStateChange('timeline_updated', {
      source,
      previousState: this._sanitizeState(previousState),
      newState: this._sanitizeState(this.currentTimelineState),
      changes: this._calculateStateDiff(previousState, this.currentTimelineState),
      timestamp
    });

    this._notifyListeners('timeline_updated', {
      previousState,
      currentState: this.currentTimelineState,
      changes: updates,
      source
    });

    this._scheduleAutoSave();
  }

  /**
   * Add conversation message to context
   */
  addConversationMessage(message, response, metadata = {}) {
    const conversationEntry = {
      id: this._generateMessageId(),
      timestamp: Date.now(),
      sessionId: this.sessionId,
      projectId: this.projectContext.projectId,
      userMessage: message,
      aiResponse: response.content || response.message,
      code: response.code || null,
      intentAnalysis: response.intentAnalysis || null,
      timelineSnapshot: this._createTimelineSnapshot(),
      metadata: {
        source: metadata.source || 'chat',
        confidence: response.confidence || metadata.confidence,
        executionTime: metadata.executionTime,
        ...metadata
      }
    };

    this.conversationContext.push(conversationEntry);

    // Limit context length
    if (this.conversationContext.length > this.maxContextLength) {
      this.conversationContext = this.conversationContext.slice(-this.maxContextLength);
    }

    this._notifyListeners('conversation_updated', conversationEntry);
    this._scheduleAutoSave();

    return conversationEntry;
  }

  /**
   * Get conversation context for AI
   */
  getConversationContext(options = {}) {
    const {
      includeCode = true,
      includeIntentAnalysis = true,
      lastN = 5,
      filterByProject = true
    } = options;

    let context = [...this.conversationContext];

    // Filter by current project
    if (filterByProject && this.projectContext.projectId) {
      context = context.filter(entry => entry.projectId === this.projectContext.projectId);
    }

    // Get last N entries
    context = context.slice(-lastN);

    return context.map(entry => ({
      timestamp: entry.timestamp,
      userMessage: entry.userMessage,
      aiResponse: entry.aiResponse,
      code: includeCode ? entry.code : null,
      intentAnalysis: includeIntentAnalysis ? entry.intentAnalysis : null,
      timelineState: entry.timelineSnapshot,
      metadata: entry.metadata
    }));
  }

  /**
   * Get enhanced context for AI code generation
   */
  getEnhancedContext() {
    return {
      // Current state
      currentTimeline: this.currentTimelineState,
      projectInfo: this.projectContext,
      
      // Recent conversation
      recentConversation: this.getConversationContext({ lastN: 3 }),
      
      // Editing patterns
      editingPatterns: this._analyzeEditingPatterns(),
      
      // User preferences
      userPreferences: this.getUserPreferences(),
      
      // Session context
      sessionInfo: {
        sessionId: this.sessionId,
        duration: Date.now() - this.lastActivity,
        isActive: this.isActive,
        editCount: this.editingHistory.length
      }
    };
  }

  /**
   * Record successful code execution
   */
  recordCodeExecution(code, result, executionTime) {
    const executionEntry = {
      id: this._generateMessageId(),
      timestamp: Date.now(),
      sessionId: this.sessionId,
      projectId: this.projectContext.projectId,
      code: code.substring(0, 500), // Truncate long code
      success: result.success,
      executionTime,
      error: result.error || null,
      timelineBefore: this._createTimelineSnapshot(),
      timelineAfter: null, // Will be filled by next state update
      metadata: {
        codeLength: code.length,
        complexity: this._estimateCodeComplexity(code),
        operations: this._extractOperations(code)
      }
    };

    this.editingHistory.push(executionEntry);

    // Limit history length
    if (this.editingHistory.length > this.maxHistoryLength) {
      this.editingHistory = this.editingHistory.slice(-this.maxHistoryLength);
    }

    this._notifyListeners('code_executed', executionEntry);
    this._scheduleAutoSave();

    return executionEntry;
  }

  /**
   * Get user preferences based on editing history
   */
  getUserPreferences() {
    if (Object.keys(this.userPreferences).length === 0) {
      this.userPreferences = this._inferUserPreferences();
    }
    return this.userPreferences;
  }

  /**
   * Update user preferences
   */
  updateUserPreferences(preferences) {
    this.userPreferences = { ...this.userPreferences, ...preferences };
    this._scheduleAutoSave();
  }

  /**
   * Get editing suggestions based on current context
   */
  getEditingSuggestions() {
    const context = this.getEnhancedContext();
    const suggestions = [];

    // Analyze current timeline state
    if (context.currentTimeline) {
      const { clips, selectedClipIds, duration } = context.currentTimeline;

      // Duration-based suggestions
      if (duration > 60) {
        suggestions.push({
          type: 'optimization',
          title: 'Consider shortening',
          description: 'Your timeline is over 1 minute. Consider cutting to key moments.',
          action: 'cut_to_duration',
          parameters: { target_duration: 30 },
          priority: 'medium'
        });
      }

      // Selection-based suggestions
      if (selectedClipIds.length > 1) {
        suggestions.push({
          type: 'multi_clip',
          title: 'Arrange selected clips',
          description: 'Create a sequence or montage from selected clips.',
          action: 'arrange_clips',
          parameters: { clipIds: selectedClipIds },
          priority: 'high'
        });
      }

      // Content-based suggestions from editing patterns
      const patterns = this._analyzeEditingPatterns();
      if (patterns.frequentOperations.includes('speed_adjustment')) {
        suggestions.push({
          type: 'pattern',
          title: 'Speed adjustment',
          description: 'You often adjust speed. Consider applying to current selection.',
          action: 'adjust_speed',
          parameters: { speed: 1.25 },
          priority: 'low'
        });
      }
    }

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  }

  /**
   * Get timeline analytics
   */
  getTimelineAnalytics() {
    if (!this.currentTimelineState) return null;

    const { clips, duration } = this.currentTimelineState;
    
    return {
      // Basic metrics
      totalClips: clips.length,
      totalDuration: duration,
      averageClipDuration: clips.length > 0 ? clips.reduce((sum, clip) => sum + clip.duration, 0) / clips.length : 0,
      
      // Content analysis
      contentTypes: this._analyzeContentTypes(clips),
      pacing: this._analyzePacing(clips),
      
      // Editing metrics
      editingSessions: this._countEditingSessions(),
      mostUsedOperations: this._getMostUsedOperations(),
      
      // Session info
      sessionDuration: Date.now() - this.lastActivity,
      editsPerMinute: this._calculateEditsPerMinute()
    };
  }

  /**
   * Subscribe to state changes
   */
  subscribe(eventType, callback) {
    const listenerId = this.listenerIdCounter++;
    
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Map());
    }
    
    this.listeners.get(eventType).set(listenerId, callback);
    
    return () => {
      this.listeners.get(eventType)?.delete(listenerId);
    };
  }

  /**
   * Create save point for undo/redo
   */
  createSavePoint(description = 'Manual save point') {
    const savePoint = {
      id: this._generateMessageId(),
      timestamp: Date.now(),
      description,
      timelineState: this._createTimelineSnapshot(),
      conversationState: [...this.conversationContext.slice(-5)], // Last 5 messages
      sessionId: this.sessionId
    };

    this._recordStateChange('save_point_created', savePoint);
    return savePoint;
  }

  /**
   * Clear conversation context
   */
  clearConversationContext() {
    this.conversationContext = [];
    this._notifyListeners('conversation_cleared', {});
    this._scheduleAutoSave();
  }

  /**
   * Export state for backup/sharing
   */
  exportState() {
    return {
      projectContext: this.projectContext,
      timelineState: this.currentTimelineState,
      conversationContext: this.conversationContext,
      editingHistory: this.editingHistory.slice(-10), // Last 10 edits
      userPreferences: this.userPreferences,
      analytics: this.getTimelineAnalytics(),
      exportedAt: Date.now(),
      version: '1.0'
    };
  }

  /**
   * Import state from backup
   */
  importState(stateData) {
    try {
      if (stateData.version !== '1.0') {
        console.warn('State version mismatch, attempting to import anyway');
      }

      this.projectContext = stateData.projectContext || {};
      this.currentTimelineState = stateData.timelineState || null;
      this.conversationContext = stateData.conversationContext || [];
      this.editingHistory = stateData.editingHistory || [];
      this.userPreferences = stateData.userPreferences || {};

      this._notifyListeners('state_imported', stateData);
      this._scheduleAutoSave();

      return true;
    } catch (error) {
      console.error('Failed to import state:', error);
      return false;
    }
  }

  // Private methods

  _generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  _generateMessageId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }

  _createTimelineSnapshot() {
    if (!this.currentTimelineState) return null;
    
    return {
      clips: this.currentTimelineState.clips.map(clip => ({
        id: clip.id,
        name: clip.name,
        duration: clip.duration,
        startTime: clip.startTime,
        tags: clip.tags || []
      })),
      duration: this.currentTimelineState.duration,
      aspectRatio: this.currentTimelineState.aspectRatio,
      selectedClipIds: [...this.currentTimelineState.selectedClipIds],
      timestamp: Date.now()
    };
  }

  _recordStateChange(type, data) {
    // This could be extended to record detailed audit logs
    console.debug(`Timeline state change: ${type}`, data);
  }

  _sanitizeState(state) {
    // Remove any sensitive or circular references for storage
    return {
      clips: state.clips?.length || 0,
      duration: state.duration,
      aspectRatio: state.aspectRatio,
      selectedCount: state.selectedClipIds?.length || 0
    };
  }

  _calculateStateDiff(prev, current) {
    const diff = {};
    
    if (prev.clips?.length !== current.clips?.length) {
      diff.clipsChanged = true;
      diff.clipCountDelta = current.clips.length - prev.clips.length;
    }
    
    if (prev.duration !== current.duration) {
      diff.durationChanged = true;
      diff.durationDelta = current.duration - prev.duration;
    }
    
    if (prev.selectedClipIds?.length !== current.selectedClipIds?.length) {
      diff.selectionChanged = true;
    }
    
    return diff;
  }

  _analyzeEditingPatterns() {
    const patterns = {
      frequentOperations: [],
      preferredDurations: [],
      editingVelocity: 0,
      sessionPatterns: {}
    };

    if (this.editingHistory.length === 0) return patterns;

    // Analyze operation frequency
    const operationCounts = {};
    this.editingHistory.forEach(entry => {
      if (entry.metadata?.operations) {
        entry.metadata.operations.forEach(op => {
          operationCounts[op] = (operationCounts[op] || 0) + 1;
        });
      }
    });

    patterns.frequentOperations = Object.entries(operationCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([op]) => op);

    // Calculate editing velocity (edits per minute)
    if (this.editingHistory.length > 1) {
      const timeSpan = this.editingHistory[this.editingHistory.length - 1].timestamp - this.editingHistory[0].timestamp;
      patterns.editingVelocity = (this.editingHistory.length / (timeSpan / 60000)); // edits per minute
    }

    return patterns;
  }

  _inferUserPreferences() {
    const preferences = {
      preferredAspectRatio: '16:9',
      preferredDuration: 30,
      preferredPacing: 'medium',
      autoSuggestionsEnabled: true,
      complexityPreference: 'simple'
    };

    // Analyze editing history to infer preferences
    if (this.editingHistory.length > 0) {
      // Infer complexity preference
      const avgComplexity = this.editingHistory.reduce((sum, entry) => 
        sum + (entry.metadata?.complexity || 1), 0) / this.editingHistory.length;
      
      preferences.complexityPreference = avgComplexity > 3 ? 'advanced' : 'simple';
    }

    return preferences;
  }

  _estimateCodeComplexity(code) {
    const lines = code.split('\n').length;
    const loops = (code.match(/for\s*\(/g) || []).length;
    const conditionals = (code.match(/if\s*\(/g) || []).length;
    const apiCalls = (code.match(/timeline\./g) || []).length;
    
    return Math.min(10, lines * 0.1 + loops * 0.5 + conditionals * 0.3 + apiCalls * 0.2);
  }

  _extractOperations(code) {
    const operations = [];
    const operationPatterns = [
      { pattern: /timeline\.addClip/g, operation: 'add_clip' },
      { pattern: /timeline\.moveClip/g, operation: 'move_clip' },
      { pattern: /timeline\.removeClip/g, operation: 'remove_clip' },
      { pattern: /timeline\.trimClip/g, operation: 'trim_clip' },
      { pattern: /timeline\.splitClip/g, operation: 'split_clip' },
      { pattern: /speed.*=/g, operation: 'speed_adjustment' },
      { pattern: /transition/gi, operation: 'transition' }
    ];

    operationPatterns.forEach(({ pattern, operation }) => {
      if (pattern.test(code)) {
        operations.push(operation);
      }
    });

    return operations;
  }

  _analyzeContentTypes(clips) {
    const contentTypes = {};
    clips.forEach(clip => {
      clip.tags?.forEach(tag => {
        contentTypes[tag] = (contentTypes[tag] || 0) + 1;
      });
    });
    return contentTypes;
  }

  _analyzePacing(clips) {
    if (clips.length === 0) return { consistency: 0, average: 0 };
    
    const durations = clips.map(clip => clip.duration);
    const average = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    const variance = durations.reduce((sum, d) => sum + Math.pow(d - average, 2), 0) / durations.length;
    
    return {
      average,
      variance,
      consistency: 1 - (Math.sqrt(variance) / average) // Higher = more consistent
    };
  }

  _countEditingSessions() {
    // Count distinct sessions based on time gaps
    let sessions = 1;
    for (let i = 1; i < this.editingHistory.length; i++) {
      const timeDiff = this.editingHistory[i].timestamp - this.editingHistory[i-1].timestamp;
      if (timeDiff > 30 * 60 * 1000) { // 30 minute gap = new session
        sessions++;
      }
    }
    return sessions;
  }

  _getMostUsedOperations() {
    const operations = {};
    this.editingHistory.forEach(entry => {
      entry.metadata?.operations?.forEach(op => {
        operations[op] = (operations[op] || 0) + 1;
      });
    });
    
    return Object.entries(operations)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([op, count]) => ({ operation: op, count }));
  }

  _calculateEditsPerMinute() {
    if (this.editingHistory.length < 2) return 0;
    
    const timeSpan = Date.now() - this.editingHistory[0].timestamp;
    return (this.editingHistory.length / (timeSpan / 60000));
  }

  _notifyListeners(eventType, data) {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${eventType} listener:`, error);
        }
      });
    }
  }

  _scheduleAutoSave() {
    if (this.autoSaveEnabled) {
      this.debouncedSave();
    }
  }

  _persistState() {
    try {
      const stateToSave = {
        projectContext: this.projectContext,
        conversationContext: this.conversationContext.slice(-10), // Last 10 messages
        editingHistory: this.editingHistory.slice(-20), // Last 20 edits
        userPreferences: this.userPreferences,
        sessionId: this.sessionId,
        lastSaved: Date.now()
      };

      localStorage.setItem(this.persistenceKey, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Failed to persist timeline state:', error);
    }
  }

  _loadPersistedState() {
    try {
      const savedState = localStorage.getItem(this.persistenceKey);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        
        this.conversationContext = parsed.conversationContext || [];
        this.editingHistory = parsed.editingHistory || [];
        this.userPreferences = parsed.userPreferences || {};
        
        // Only restore project context if it's recent (within 24 hours)
        if (parsed.lastSaved && (Date.now() - parsed.lastSaved) < 24 * 60 * 60 * 1000) {
          this.projectContext = parsed.projectContext || {};
        }
      }
    } catch (error) {
      console.error('Failed to load persisted state:', error);
    }
  }

  _loadProjectConversationContext(projectId) {
    // Filter conversation context to only include current project
    this.conversationContext = this.conversationContext.filter(
      entry => entry.projectId === projectId
    );
  }

  _setupSessionCleanup() {
    // Clean up old sessions and expired data
    setInterval(() => {
      const now = Date.now();
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days

      // Clean conversation context
      this.conversationContext = this.conversationContext.filter(
        entry => (now - entry.timestamp) < maxAge
      );

      // Clean editing history
      this.editingHistory = this.editingHistory.filter(
        entry => (now - entry.timestamp) < maxAge
      );

    }, 60 * 60 * 1000); // Run every hour
  }
}

// Export singleton instance
const timelineStateManager = new TimelineStateManager();
export default timelineStateManager;